var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Slider, { SliderThumb } from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SLIDER_STEP, SLIDER_THUMB_SIZE, SLIDER_TRACK_HEIGHT } from './constants';
import { selectIsValidSliderChange, selectSliderInfo, selectTrackSegments, selectVarianceFromValues, } from './metricSliderSelectors';
export var AxisType;
(function (AxisType) {
    AxisType["POSITIVE_AXIS"] = "positiveAxis";
    AxisType["REAL_AXIS"] = "realAxis";
})(AxisType || (AxisType = {}));
// Styled components
var StyledSliderThumb = styled(SliderThumb)(function (_a) {
    var theme = _a.theme;
    return ({
        background: theme.palette.background.paper,
        padding: 0,
        width: SLIDER_THUMB_SIZE,
        height: SLIDER_THUMB_SIZE,
        '&:before': { boxShadow: 'none' },
        filter: "drop-shadow(0 0 4px ".concat(theme.palette.action.disabled, ")"),
    });
});
var Arrow = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderLeftWidth: theme.typography.pxToRem(4),
        borderRightWidth: theme.typography.pxToRem(4),
        borderColor: 'transparent',
        borderTopWidth: theme.typography.pxToRem(6),
        borderTopColor: theme.palette.text.secondary,
    });
});
var SliderLabel = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.text.secondary,
        fontWeight: 700,
        width: theme.typography.pxToRem(148),
        maxWidth: theme.typography.pxToRem(148),
        textAlign: 'right',
    });
});
var TrackSegment = styled(Box)(function (_a) {
    var left = _a.left, width = _a.width, backgroundColor = _a.backgroundColor, borderRadius = _a.borderRadius;
    return ({
        position: 'absolute',
        left: "".concat(left, "%"),
        width: "".concat(width, "%"),
        height: SLIDER_TRACK_HEIGHT,
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: backgroundColor,
        borderRadius: borderRadius,
    });
});
var StyledSlider = styled(Slider)(function (_a) {
    var theme = _a.theme, _b = _a.disabled, disabled = _b === void 0 ? false : _b;
    return (__assign({ marginBottom: 0, position: 'absolute', width: '100%', height: SLIDER_TRACK_HEIGHT, top: '50%', transform: 'translateY(-50%)', '& .MuiSlider-rail': { opacity: 0 }, '& .MuiSlider-track': { display: 'none' }, '& .MuiSlider-mark': { display: 'none' }, '& .MuiSlider-thumb': {
            '&:focus, &:hover, &.Mui-focusVisible': {
                boxShadow: "0px 0px 0px 6px ".concat(theme.palette.action.focus),
            },
        }, '& .MuiSlider-markLabel': {
            color: theme.palette.text.primary,
            top: theme.typography.pxToRem(-32),
            transform: 'none',
            '&[data-index="0"]': { left: 0 },
            '&[data-index="1"]': { top: theme.typography.pxToRem(-16) },
            '&[data-index="2"]': { right: 0 },
        } }, (disabled && {
        pointerEvents: 'none',
        cursor: 'not-allowed',
    })));
});
var SliderContainer = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        height: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(7),
        marginTop: theme.typography.pxToRem(36),
    });
});
var CustomSliderThumb = function (props) {
    var children = props.children, otherProps = __rest(props, ["children"]);
    var idx = props['data-index'];
    return (_jsxs(StyledSliderThumb, __assign({}, otherProps, { children: [children, _jsx(Typography, __assign({ fontSize: function (theme) { return theme.typography.pxToRem(11); }, fontWeight: 'bold', lineHeight: 1, color: function (theme) { return theme.palette.text.primary; } }, { children: props.ownerState.value[idx] }))] })));
};
var TrackSegments = function (_a) {
    var segments = _a.segments, min = _a.min, max = _a.max;
    return (_jsx(_Fragment, { children: segments.map(function (seg, i) {
            var left = ((seg.start - min) / (max - min)) * 100;
            var width = ((seg.end - seg.start) / (max - min)) * 100;
            var isEdge = i === 0 || i === segments.length - 1;
            return (_jsx(TrackSegment, { left: left, width: width, backgroundColor: seg.color, borderRadius: isEdge ? 4 : 0 }, "segment-".concat(seg.start, "-").concat(seg.color)));
        }) }));
};
var createMarks = function (min, max, minLabel, maxLabel, axisType) {
    return [
        {
            value: min,
            label: (_jsxs(Stack, { children: [_jsx(Typography, __assign({ fontSize: 10 }, { children: minLabel })), min !== 0 && _jsxs(Typography, __assign({ fontSize: 14 }, { children: ["<\u00A0", min] }))] })),
        },
        {
            value: 0,
            label: (_jsxs(Stack, __assign({ style: { transform: axisType === AxisType.REAL_AXIS ? 'translateX(-50%)' : 'none' } }, { children: [_jsx(Typography, __assign({ fontSize: 14 }, { children: "0" })), _jsx(Arrow, {})] }))),
        },
        {
            value: max,
            label: (_jsxs(Stack, __assign({ alignItems: "flex-end" }, { children: [_jsx(Typography, __assign({ fontSize: 10 }, { children: maxLabel })), _jsxs(Typography, __assign({ fontSize: 14 }, { children: [">\u00A0", max] }))] }))),
        },
    ];
};
var MetricSlider = function (_a) {
    var ranges = _a.ranges, _b = _a.step, step = _b === void 0 ? SLIDER_STEP : _b, metricType = _a.metricType, onChange = _a.onChange, children = _a.children, _c = _a.disabled, disabled = _c === void 0 ? false : _c;
    var t = useTranslation('report', { keyPrefix: 'actual' }).t;
    var _d = selectSliderInfo(ranges), axisType = _d.axisType, min = _d.min, max = _d.max, initialValues = _d.initialValues;
    var _e = useState(initialValues), values = _e[0], setValues = _e[1];
    var segments = selectTrackSegments(values, min, max, axisType);
    var marks = createMarks(min, max, t('manageSettings.minLabel'), t('manageSettings.maxLabel'), axisType);
    var handleChange = useCallback(function (_, newValue, activeThumb) {
        var updatedValues = newValue;
        if (!selectIsValidSliderChange(updatedValues, activeThumb, axisType)) {
            return;
        }
        setValues(updatedValues);
        var newMetadata = selectVarianceFromValues(updatedValues, min, max, axisType);
        onChange(newMetadata);
    }, [axisType, onChange, min, max]);
    return (_jsxs(Stack, __assign({ sx: { height: '100%' }, gap: 4 }, { children: [_jsxs(SliderContainer, { children: [_jsx(SliderLabel, { children: t("metrics.".concat(metricType)) }), _jsxs(Box, __assign({ sx: { position: 'relative', flex: 1 } }, { children: [_jsx(TrackSegments, { segments: segments, min: min, max: max }), _jsx(StyledSlider, { min: min, max: max, value: values, onChange: handleChange, slots: { thumb: CustomSliderThumb }, disableSwap: true, marks: marks, step: step, disabled: disabled })] }))] }), children && _jsx(_Fragment, { children: children })] })));
};
export default MetricSlider;
